import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';
import { laserDustFragmentShader } from '../lighting/laser-array.js';
import { HASH_CHUNK, buildFieldChunk } from './caustics.js';

// Kelp - a laminaria forest from the sea floor, looking up at the surface.
// Ambient, downtempo, classical, anything contemplative.
//
// THE WHOLE IMAGE IS ONE FORMULA, and it is not a palette. Water absorbs red
// first, then green, and keeps blue:
//
//   depth = clamp(-worldY / uWaterDepth, 0, 1)
//   water = exp(-depth * vec3(0.55, 0.30, 0.18) * uTurbidity)
//
// applied to EVERY layer - blades, rays, particles, floor, surface. Nothing in
// this file picks a blue-green anywhere; the blue-green is what is left of
// white light after that exponential. The bottom of the frame is nearly black
// and the top is bright for the same reason, and the album palette can only
// tint what survives.
//
// Y IS ZERO AT THE SURFACE AND NEGATIVE GOING DOWN, which is what makes the
// formula above read literally. The sea floor is at -WATER_DEPTH and the
// camera sits just above it.
//
// THE SWELL TRAVELS. A blade does not swing like a pendulum hinged at the
// bottom; the swell runs UP it:
//
//   displacement(s, t) = A * s^1.6 * sin(w*t - k*s + phase)
//
// The -k*s term is the whole point - it is what makes the crest arrive at the
// base before the tip. s^1.6 is what keeps the holdfast almost still. Every
// blade shares w and k and differs only in phase, and that phase is read off
// its position along the swell direction, so the forest moves as one current
// rather than as forty independent plants.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'kelp',
  family: 'organic',
  energyLevel: 0.22,
  bpmMin: 40,
  bpmMax: 125,
  movement: 0.35,
  danceability: 0.20,
  tonality: 0.92,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.45] },
  enabled: true
};

// --- the water -------------------------------------------------------------
const WATER_DEPTH = 18.0;
// The brief gives the absorption coefficients but not the turbidity they are
// scaled by, and that scalar is what decides whether the frame has a gradient
// at all: at 1.35 the floor still keeps 48% of the red and the whole image is
// a pale wash. At 4.0 the floor keeps 11% red / 30% green / 49% blue, which
// is a dark blue-green bottom under a bright top - the image the brief asks
// for, and the ordering (red dies first) is the formula's own.
const TURBIDITY = 4.0;
// ABSORPTION ALSO HAPPENS ON THE WAY TO THE EYE, and leaving that out is what
// made the first build white. The brief's formula is a function of the shaded
// point's DEPTH, which is right for the light coming down - but the surface
// is at depth 0 everywhere, so by that formula alone a surface plane 60 units
// away is as bright as the patch directly overhead, and it filled the frame.
// The same coefficients are therefore applied a second time over the distance
// from the point to the camera. This is what makes the far forest recede into
// blue and the far surface go dark, both of which the brief asks for.
// 0.80, not the 0.42 it was first built with. The camera looks UP at 55
// degrees with a 62-degree lens, so every pixel of the frame is above the
// horizon and therefore shows the surface - and the surface sits at depth 0,
// where the brief's own formula does nothing. The only thing that can darken
// the bottom of the frame is the slant path: 40 units of water at 24 degrees
// elevation against 16 units at 86. At 0.42 that gradient measured 0.41
// against 0.47 and the frame was flat; this is what makes the bottom dark.
const VIEW_ATTEN = 0.80;
const FLOOR_Y = -WATER_DEPTH;

// --- the camera ------------------------------------------------------------
// On the floor, looking up at 55 degrees. One revolution in 240s and a 9s
// pitch bob, like a diver holding station.
const CAM_Y = FLOOR_Y + 1.5;
const CAM_PITCH = 55 * Math.PI / 180;
const PAN_PERIOD = 240.0;
const BOB_PERIOD = 9.0;
const BOB_AMOUNT = 2.6 * Math.PI / 180;
const CAMERA_FOV = 62;

// --- the forest ------------------------------------------------------------
const TIER_LAYOUT = {
  2: { blades: 40, rays: 12, caustFrond: true, caustFloor: true, surface: true, doubleSide: true, particles: 600, octaves: 3, surfSegs: 48 },
  1: { blades: 26, rays: 8, caustFrond: false, caustFloor: true, surface: true, doubleSide: true, particles: 380, octaves: 2, surfSegs: 28 },
  // The surface stays at every tier. Cutting it was a mistake: it is not
  // decoration, it is the only light in the scene, and without it tier 0
  // measured a mean luminance of 0.008 - a black frame. What tier 0 drops
  // is its TESSELLATION (see surfSegs), which is what actually costs.
  0: { blades: 16, rays: 5, caustFrond: false, caustFloor: false, surface: true, doubleSide: false, particles: 220, octaves: 2, surfSegs: 12 }
};
const DEFAULT_TIER = 2;

const BLADE_SEGMENTS = 24;
// The frond is ONE ribbon subdivided across its width into five strips. Below
// SPLIT_S they are flush and read as a single sheet; above it they separate
// into lanières. They are separate strips of geometry rather than a shared
// mesh precisely so they CAN come apart - and they take their normal from the
// spine, not from themselves, so the frond does not facet where they meet.
const STRIPS = 5;
const SPLIT_S = 0.64;
const LANIERE_SEP = 0.28;      // how far the lanières spread, in half-widths
const LANIERE_FLUT = 0.55;
const LANIERE_T = 3.1;
const LANIERE_K = 5.4;
// The lasagne edge: a ruffle in the WIDTH along the frond, and a buckle out
// of plane that is strongest at the edges and zero on the midrib.
const FROND_WIDTH_RUFFLE = 0.30;
const EDGE_RUFFLE = 0.55;
const BLADE_MIN_H = 6.0;
const BLADE_MAX_H = 14.0;
// Half-width, so the widest fronds are 1.2 units across.
const BLADE_HALF_MAX = 0.60;
const BLADE_MIN_R = 4.5;       // no blade closer than this to the camera
const BLADE_MAX_R = 24.0;

// --- the swell -------------------------------------------------------------
// Two swells at incommensurable periods, so the forest never repeats.
const SWELL_T1 = 7.0;
const SWELL_T2 = 11.0;
const SWELL_A1 = 0.70;
const SWELL_A2 = 0.38;
const SWELL_K1 = 3.1;          // radians of phase per unit of s: the travel
const SWELL_K2 = 4.7;
const SWELL_EXP = 1.6;         // s^1.6 - the base barely moves
const SWELL_SPATIAL_K = 0.11;  // radians per world unit along the swell

// --- the blades ------------------------------------------------------------
// A LAMINARIA IS BROWN-OLIVE, not green. Saturation 0.40 and lightness 0.25,
// which is a dark olive, and the palette may only tint it - 30% at most, so
// it can never become a lawn.
const BLADE_BASE_HUE = 0.11;
const BLADE_SAT = 0.40;
const BLADE_LIGHTNESS = 0.25;
const PALETTE_TINT = 0.30;
const PALETTE_OPPOSITE = 0.42;
// THE TRANSMITTED LIGHT IS TINTED BY THE FROND ITSELF - daylight times the
// blade's own brown-olive, warmed a little towards gold. That product IS the
// green-gold: blue-green water light through a warm brown sheet. Using a
// separate green tint instead, as the first build did, made every frond the
// same pale sage whatever colour the plant was, and the palette stopped
// mattering at all.
// The warm push is SMALL. At 1.35 on red the transmitted red came out equal
// to the green, ACES desaturated the highlight, and every frond went cream.
const TRANSMIT_WARM = [1.28, 1.00, 0.50];
// And the gain is what decides whether the plant is olive or a lamp. At 9.0
// the whole frond blew out to near-white; this lands the midrib around 0.33
// displayed and the thin backlit edges near 0.52.
const TRANS_GAIN = 7.0;
const TRANS_CENTER = 0.38;     // the midrib is thick: little gets through
const TRANS_EDGE = 1.55;       // the edge is thin: most does
const TRANS_BACK = 2.20;       // the face turned away from the surface
// How much of the frond's own brown-olive is reflected back at us. It has
// to hold its own against the transmission or the plant is just a lamp.
const BODY_GAIN = 6.0;

// --- the rays --------------------------------------------------------------
const RAY_NEAR_PX = 40.0;
const RAY_FAR_PX = 120.0;
const RAY_REFRACT_PERIOD = 5.0;
const RAY_REFRACT_AMP = 6 * Math.PI / 180;
const RAY_GAIN = 0.30;
const RAY_PEAK = 0.92;

// --- caustics --------------------------------------------------------------
const CAUST_PERIOD = 4.0;
// THE NET HAS TO BE FINER THAN A FROND OR IT CANNOT BE SEEN ON ONE. At 0.30
// the caustic cells are about three world units across and a blade half a
// unit wide sits inside a single cell: measured against a tier with the frond
// caustics compiled out, the two were identical to within 0.3%, i.e. the
// caustics were doing nothing at all on the kelp. At 1.2 the cells are about
// eight tenths of a unit and a frond carries real banding.
const CAUST_SCALE = 0.70;
// The net is COARSER on the floor than on the fronds, and that is not a
// cheat: the rays that form it diverge on the way down, so its world-space
// wavelength grows with depth. Same reason caustics.js scales its own domain
// by the apparent depth.
const CAUST_SCALE_FLOOR = 0.42;
const CAUST_GAIN = 0.85;
const CAUST_SHARP = 0.55;
const CAUST_CONVERGE = 0.50;
// The caustic's own gain on the FRONDS, set separately rather than derived
// from the transmission. Measured by rendering the caustic term alone out of
// the shipped shader: the fold reaches about 0.035 on a blade, which at the
// derived coefficient modulated the frond by 4% - computed, and invisible.
// Swept on the live material through uCaustGain: at an effective 8 the
// caustic lifted the fronds' mean by 1.1% and their spatial variation not at
// all - present in the arithmetic, absent from the image. At 80 it lifts the
// mean 8.6% and the across-frond variation 6.7%, which is a pattern you can
// see travelling. TRANS_GAIN comes down to keep the overall level.
const CAUST_FROND_GAIN = 52.0;

// --- particles -------------------------------------------------------------
const PARTICLE_REVEAL_R = 2.2;
const BUBBLE_FRACTION = 0.04;

// The shared safe-zone ramp bottoms out at SAFE_MIN_ATTENUATION = 0.32 in
// shaders/safeZone.glsl.js. The brief asks for 0.5 here. Remapped, never
// edited at the source - every other visualizer depends on that constant.
const SHARED_SAFE_FLOOR = 0.32;
const SAFE_FLOOR = 0.50;

// How far a near blade is pushed aside when the camera swings onto it, and
// how wide the wedge that triggers it. The camera turns 1.5 degrees a second,
// so a blade spends about twenty seconds inside the wedge and the push is far
// too slow to read as motion - it just means no broad frond is ever standing
// on the cover.
const NEAR_PUSH = 9 * Math.PI / 180;
const NEAR_WEDGE = 22 * Math.PI / 180;

const DEG = Math.PI / 180;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

// THREE.ColorManagement IS OFF IN THIS BUILD, so Color.setHSL stores the sRGB
// triple RAW - it does not convert to the linear working space. Measured, by
// reading uBladeColor back out of the live material: HSL(0.11, 0.40, 0.25)
// arrived in the shader as (0.35, 0.282, 0.15) instead of (0.102, 0.065,
// 0.019). That is three and a half times too bright and far too desaturated,
// and it is why a brown-olive blade rendered as pale sage. The conversion is
// therefore done here, explicitly, and the colour is written with setRGB.
function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function setLinearFromHSL(color, h, sat, light) {
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const hp = (((h % 1) + 1) % 1) * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp < 1) { r = c; g = x; }
  else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; }
  else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const m = light - c / 2;
  color.setRGB(srgbToLinear(r + m), srgbToLinear(g + m), srgbToLinear(b + m));
  return color;
}

// ---------------------------------------------------------------------------
// Shared GLSL
// ---------------------------------------------------------------------------

// The one formula the whole visualizer is built on.
const WATER_CHUNK = `
  uniform float uWaterDepth;
  uniform float uTurbidity;

  // 0 at the surface, 1 on the floor.
  float waterDepth01(float worldY) {
    return clamp(-worldY / uWaterDepth, 0.0, 1.0);
  }

  // The brief's formula: how much of the surface light is still there at this
  // depth. Red goes first, then green, blue survives - this is the entire
  // colour scheme of the visualizer, and nothing here picks a blue-green.
  vec3 lightAtDepth(float worldY) {
    return exp(-waterDepth01(worldY) * vec3(0.55, 0.30, 0.18) * uTurbidity);
  }

  // And the second pass of the same absorption, over the distance from the
  // point to the eye. See VIEW_ATTEN.
  vec3 viewAtten(vec3 worldPos) {
    float d = length(cameraPosition - worldPos) / uWaterDepth;
    return exp(-d * ${VIEW_ATTEN.toFixed(3)} * vec3(0.55, 0.30, 0.18) * uTurbidity);
  }

  // Both, for anything that is lit by the surface and then looked at.
  vec3 waterAtten(vec3 worldPos) {
    return lightAtDepth(worldPos.y) * viewAtten(worldPos);
  }

  // ?kelpdepth=1. Shows BOTH paths, because showing only the brief's
  // depth term paints the whole frame white - the surface fills it and the
  // surface is at depth 0 everywhere.
  vec3 depthDebug(vec3 worldPos) {
    return vec3(dot(waterAtten(worldPos), vec3(0.3333)));
  }
`;

// The safe zone on its own floor.
const SAFE_CHUNK = `
  float kelpSafeAtt(vec4 clipPos) {
    float a = safeZoneAttenuation(clipPos);
    return ${SAFE_FLOOR.toFixed(2)}
         + (a - ${SHARED_SAFE_FLOOR.toFixed(2)})
         * ${((1 - SAFE_FLOOR) / (1 - SHARED_SAFE_FLOOR)).toFixed(6)};
  }
`;

// The swell, and where a blade is at a given s. Shared by the blade shader
// and by the JS-side acceptance harness, which evaluates the same arithmetic.
const SWELL_CHUNK = `
  uniform float uTime;
  uniform vec2 uSwellAmp;      // the two swells' amplitudes, band-modulated
  uniform vec2 uSwellDir;      // unit, in xz - the current's direction
  uniform float uCamHeading;

  // The travelling swell. s is 0 at the holdfast and 1 at the tip.
  //  - s^1.6 keeps the holdfast still and gives the tip its throw
  //  - the -k*s term is what makes the crest TRAVEL up the blade instead of
  //    the blade swinging as one piece
  vec2 swellAt(float s, float phase) {
    float env = pow(s, ${SWELL_EXP.toFixed(2)});
    float d1 = uSwellAmp.x * env
             * sin(${(2 * Math.PI / SWELL_T1).toFixed(6)} * uTime - ${SWELL_K1.toFixed(3)} * s + phase);
    float d2 = uSwellAmp.y * env
             * sin(${(2 * Math.PI / SWELL_T2).toFixed(6)} * uTime - ${SWELL_K2.toFixed(3)} * s + phase * 1.37 + 2.1);
    // Mostly along the current, with a smaller cross component so the forest
    // is not a flat sheet.
    vec2 perp = vec2(-uSwellDir.y, uSwellDir.x);
    return uSwellDir * (d1 + d2) + perp * (d2 * 0.45 - d1 * 0.25);
  }

  // The near blades are eased out of the camera's forward wedge, so that no
  // broad frond ever stands across the cover. See NEAR_PUSH.
  vec2 nearPush(vec2 xz, float isNear) {
    if (isNear < 0.5) return xz;
    float a = atan(xz.x, xz.y);
    float d = a - uCamHeading;
    d = atan(sin(d), cos(d));                    // wrapped to [-pi, pi]
    float w = 1.0 - smoothstep(0.0, ${NEAR_WEDGE.toFixed(4)}, abs(d));
    float push = sign(d == 0.0 ? 1.0 : d) * w * ${NEAR_PUSH.toFixed(4)};
    float na = a + push;
    float r = length(xz);
    return vec2(sin(na), cos(na)) * r;
  }
`;

// ---------------------------------------------------------------------------
// The blades
// ---------------------------------------------------------------------------

function makeBladeVertexShader() {
  return `
  precision mediump float;

  attribute float aS;        // 0 at the holdfast, 1 at the tip
  attribute float aU;        // -1..1 across the WHOLE frond
  attribute float aStrip;    // which lanière this vertex belongs to

  attribute vec2 iPos;
  attribute float iHeight;
  attribute float iHalf;     // half-width of the frond at its widest
  attribute float iPhase;
  attribute float iHue;
  attribute float iNear;

  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vU;
  varying float vS;
  varying float vSafe;

  ${WATER_CHUNK}
  ${SWELL_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  float bladeHash(float n) { return fract(sin(mod(n, 6283.1853)) * 43758.5453); }

  // THE PROFILE IS THE PLANT. A laminaria is a narrow stipe that opens into a
  // broad flat blade - not a stem of constant width. Half-width goes from 10%
  // at the holdfast to full by 55% of the way up, and the widest fronds are
  // 1.2 units across.
  float frondHalf(float s) {
    float w = 0.09 + 0.91 * smoothstep(0.0, 0.36, s);
    // The lasagne edge: a low-frequency wave in the WIDTH along the frond, so
    // the outline ruffles instead of running straight.
    w *= 1.0 + ${FROND_WIDTH_RUFFLE.toFixed(3)} * sin(s * 7.3 + iPhase * 2.1)
             + ${(FROND_WIDTH_RUFFLE * 0.55).toFixed(3)} * sin(s * 17.9 - iPhase * 1.3);
    // Each lanière ends at its own height, so the tip is ragged rather than
    // cut square across. The range is DELIBERATELY narrow: at 0.64..1.26 the
    // early-ending strips took the frond's width away with them from the
    // split upward and the whole plant tapered to a spear - the opposite of
    // a blade that widens towards the surface.
    float tipEnd = 0.86 + 0.14 * bladeHash(aStrip * 12.9 + iPhase * 3.7);
    w *= 1.0 - smoothstep(tipEnd - 0.06, tipEnd + 0.03, s);
    return iHalf * w;
  }

  vec3 spineAt(float s, vec2 base) {
    vec2 off = swellAt(s, iPhase);
    return vec3(base.x + off.x, ${FLOOR_Y.toFixed(2)} + iHeight * s, base.y + off.y);
  }

  void main() {
    vec2 base = nearPush(iPos, iNear);

    vec3 centre = spineAt(aS, base);
    vec3 tangent = normalize(spineAt(min(aS + 0.04, 1.0), base)
                           - spineAt(max(aS - 0.04, 0.0), base));
    // The frame is taken from the SPINE, not per lanière, so the five strips
    // share one normal below the split and the frond does not facet.
    vec3 across = normalize(cross(tangent, vec3(0.0, 0.0, 1.0)) + vec3(0.0001, 0.0, 0.0));
    vec3 nrm = normalize(cross(across, tangent));

    float half_ = frondHalf(aS);

    // THE LANIÈRES. Over the last third the frond splits: each strip drifts
    // sideways and flutters on its own phase. Below the split they are flush
    // and read as one ribbon.
    float split = smoothstep(${SPLIT_S.toFixed(2)}, 0.99, aS);
    float stripC = (aStrip + 0.5) / ${STRIPS.toFixed(1)} * 2.0 - 1.0;
    float sep = split * ${LANIERE_SEP.toFixed(3)} * stripC * iHalf;
    float flut = split * ${LANIERE_FLUT.toFixed(3)} * iHalf * uSwellAmp.x
               * sin(${(2 * Math.PI / LANIERE_T).toFixed(6)} * uTime
                     - ${LANIERE_K.toFixed(2)} * aS + aStrip * 2.39 + iPhase * 1.7);

    // The ruffle, out of plane and strongest at the edges - a flat ribbon
    // with a straight edge reads as plastic.
    float rAmp = ${EDGE_RUFFLE.toFixed(3)} * iHalf
               * (sin(aS * 9.1 + iPhase * 3.1) + 0.6 * sin(aS * 21.3 - iPhase * 2.2));
    float ruffle = rAmp * aU * aU;
    float dRdu = rAmp * 2.0 * aU;

    vec3 world = centre
               + across * (half_ * aU + sep)
               + nrm * (ruffle + flut);

    vNormal = normalize(nrm - across * dRdu);
    vWorld = world;
    vU = aU;
    vS = aS;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    vSafe = kelpSafeAtt(gl_Position);
  }
`;
}

function makeBladeFragmentShader(opts) {
  return `
  precision mediump float;
  #ifdef GL_FRAGMENT_PRECISION_HIGH
    #define CDOM highp
  #else
    #define CDOM mediump
  #endif

  uniform vec3 uBladeColor;
  uniform vec3 uTransWarm;
  uniform vec3 uSunDir;
  uniform float uDepthDebug;
  uniform float uFieldTime;
  uniform float uCaustGain;

  varying vec3 vWorld;
  varying vec3 vNormal;
  varying float vU;
  varying float vS;
  varying float vSafe;

  ${WATER_CHUNK}
  ${opts.caustics ? HASH_CHUNK : ''}
  ${opts.caustics ? buildFieldChunk('kelpField', opts.octaves) : ''}

  void main() {
    if (uDepthDebug > 0.5) { gl_FragColor = vec4(depthDebug(vWorld), 1.0); return; }

    vec3 N = normalize(vNormal);
    vec3 V = normalize(cameraPosition - vWorld);

    // The daylight that has reached this depth. It is the only light there is.
    vec3 daylight = lightAtDepth(vWorld.y);

    // THE SIGNATURE: a laminaria is a thin translucent sheet, and what you see
    // from underneath is the surface light coming THROUGH it, tinted by the
    // blade. That is the green-gold, and it is what makes the fronds high in
    // the frame the brightest things in the image rather than dark cut-outs
    // against the brightness.
    //
    // The edges are thinner than the midrib, so more light gets through them.
    float thin = mix(${TRANS_CENTER.toFixed(2)}, ${TRANS_EDGE.toFixed(2)}, pow(abs(vU), 0.8));
    // The back face is the side turned away from the surface: what reaches
    // the camera through it is almost purely transmitted.
    float backBoost = gl_FrontFacing ? 1.0 : ${TRANS_BACK.toFixed(2)};
    // We see through the blade in proportion to how much of its face we are
    // shown; edge-on there is nothing to see through.
    float face = abs(dot(N, V));

    vec3 transmitted = daylight * uBladeColor * uTransWarm * ${TRANS_GAIN.toFixed(2)}
                     * thin * backBoost * mix(0.30, 1.0, face);

    // The blade's own body colour, reflected. Dark: it is a brown-olive
    // sheet in deep water, and it is NOT what carries the image.
    vec3 reflected = uBladeColor * daylight * ${BODY_GAIN.toFixed(2)}
                   * (0.30 + 0.70 * clamp(dot(N, -uSunDir), 0.0, 1.0));

    // Veins and blotches, at the edge of visibility.
    float vein = 0.5 + 0.5 * sin(vU * 11.0 + vS * 2.0);
    float blotch = 0.5 + 0.5 * sin(vS * 19.0 + vU * 3.0);
    float texture_ = 1.0 + 0.09 * (vein - 0.5) + 0.06 * (blotch - 0.5);

    vec3 col = (reflected + transmitted) * texture_;

    ${opts.caustics ? `
    // Caustics projected from ABOVE in WORLD space, so the frond slides under
    // a pattern that is fixed in the water - which is why they travel across
    // it as it bends instead of being painted on it.
    // WHERE ON THE SURFACE the ray lighting this point came from. Projecting
    // the net straight down gives a vertical frond no pattern whatsoever: its
    // whole height sits inside one cell of xz, so every point on the blade
    // samples the same value and the caustic becomes a constant offset.
    // Measured with the term switched on and off on the same scene: 0.0%
    // difference. It is the SUN'S OBLIQUITY that writes a caustic onto a
    // standing blade, and the net's wavelength also grows with depth - the
    // same two things caustics.js builds its own domain from.
    float toSurf = max(-vWorld.y, 0.0);
    vec2 entry = vWorld.xz - uSunDir.xz * (toSurf / max(-uSunDir.y, 0.35));
    CDOM vec2 cp = entry * (${CAUST_SCALE.toFixed(4)}
                            / (0.45 + 0.55 * waterDepth01(vWorld.y)));
    vec3 H;
    kelpField(cp, H);
    float trH = H.x + H.z;
    float detH = H.x * H.z - H.y * H.y;
    // CUBING IS WHAT ERASED THEM. Swept on the live shader: at d = 1.6 the
    // fold already covers 28% of the blade pixels with a mean of 0.18, so the
    // caustic was reaching the fronds all along - and then cube(0.18) = 0.006
    // took it back out again. Squared, and with a gentler convergence, it
    // covers 57% at a mean of 0.34, which is a broad moving pattern rather
    // than a razor line that mostly misses a blade a few pixels wide.
    float d = ${CAUST_CONVERGE.toFixed(2)};
    float j = 1.0 + d * (d * detH - trH);
    float caust = clamp(1.0 - abs(j) * ${CAUST_SHARP.toFixed(3)}, 0.0, 1.0);
    caust *= caust;
    // Gated on facing the SUN, not on facing straight up: a frond is a
    // vertical sheet and dot(N, up) is zero on every one of them.
    float lit = 0.35 + 0.65 * clamp(dot(N, -uSunDir), 0.0, 1.0);
    // Added to the TRANSMITTED light, because that is what a caustic landing
    // on a translucent sheet does - it shines through.
    col += daylight * uBladeColor * uTransWarm * caust * lit
         * uCaustGain * ${CAUST_FROND_GAIN.toFixed(1)};
    ` : ''}

    col *= viewAtten(vWorld) * vSafe;
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

// ---------------------------------------------------------------------------
// The rays
// ---------------------------------------------------------------------------

const rayVertexShader = `
  precision mediump float;

  attribute float iRay;
  attribute float aSide;     // -1 / +1 across the shaft
  attribute float aSpan;     // 0 at the surface, 1 at the far end

  uniform float uTime;
  uniform float uPixelScale;
  uniform float uRayCount;
  uniform float uRayFade;        // the phrase's extra ray, easing in or out
  uniform float uSunTilt;        // from progress: oblique, vertical, oblique
  uniform float uSunAz;
  uniform float uAgitation;      // bands.mid
  uniform vec2 uSwellAmp;

  varying vec2 vUv;
  varying float vGain;
  varying vec3 vWorld;
  varying float vSafe;

  ${WATER_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  void main() {
    float fi = iRay;
    float az = uSunAz + fi * 2.39996;            // golden angle, never clumps
    // REFRACTION: the surface's own slope turns each shaft slowly. Period 5s,
    // amplitude 6 degrees, a different phase per shaft, so they sweep rather
    // than swing together.
    float wob = ${RAY_REFRACT_AMP.toFixed(5)} * (1.0 + uAgitation * 0.8)
              * sin(${(2 * Math.PI / RAY_REFRACT_PERIOD).toFixed(6)} * uTime + fi * 1.7);
    float tilt = uSunTilt + wob;

    vec3 dir = normalize(vec3(sin(tilt) * sin(az), -cos(tilt), sin(tilt) * cos(az)));

    // Entry point on the surface, spread around the diver.
    float r = 4.0 + mod(fi * 6.3, 17.0);
    vec3 origin = vec3(sin(az * 1.7) * r, 0.0, cos(az * 1.7) * r);

    float t = aSpan;
    vec3 along = origin + dir * (t * ${(WATER_DEPTH * 1.45).toFixed(2)});

    vec3 toCam = cameraPosition - along;
    float camDist = length(toCam);
    toCam = camDist > 0.0001 ? toCam / camDist : vec3(0.0, 0.0, 1.0);
    // A far harder guard than laser-array's 0.30 floor, and it has to be. The
    // shafts point DOWN and the camera looks UP, so they spend most of their
    // time close to anti-parallel with the view - the one case where a
    // camera-facing quad degenerates into a screen-filling smear. At 0.30
    // they rendered as flat pale slabs with hard straight edges.
    float align = abs(dot(dir, toCam));
    float guard = mix(0.04, 1.0, 1.0 - smoothstep(0.72, 0.985, align));
    vec3 side = cross(dir, toCam);
    float sl = length(side);
    side = sl > 0.0001 ? side / sl : vec3(1.0, 0.0, 0.0);

    vec4 mv = modelViewMatrix * vec4(along, 1.0);
    float viewDepth = max(-mv.z, 0.001);
    float worldPerPx = viewDepth / uPixelScale;
    float halfW = mix(${RAY_NEAR_PX.toFixed(1)}, ${RAY_FAR_PX.toFixed(1)}, t) * 0.5 * worldPerPx;

    vec3 world = along + side * (halfW * aSide);

    // The surface gathers and spills light as the swell passes over it.
    float breathe = 0.62 + 0.38 * sin(${(2 * Math.PI / SWELL_T1).toFixed(6)} * uTime * 0.5 + fi * 2.1)
                  * (0.6 + 0.4 * uSwellAmp.x);
    // The phrase's extra shaft fades the LAST one in and out.
    // "active" is a RESERVED WORD in GLSL ES - same family as "half" and
    // "patch". A backtick cannot appear here either: this GLSL lives inside a
    // JS template literal, and one would close it.
    float onNow = step(fi, uRayCount - 0.5);
    float edgeRay = step(uRayCount - 1.5, fi) * (1.0 - uRayFade);
    onNow *= 1.0 - edgeRay;

    vUv = vec2(aSide, t);
    vWorld = world;
    vGain = guard * breathe * onNow;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
    vSafe = kelpSafeAtt(gl_Position);
  }
`;

const rayFragmentShader = `
  precision mediump float;

  uniform float uDepthDebug;

  varying vec2 vUv;
  varying float vGain;
  varying vec3 vWorld;
  varying float vSafe;

  ${WATER_CHUNK}

  void main() {
    vec3 water = waterAtten(vWorld);
    if (uDepthDebug > 0.5) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }

    // A very soft profile - a shaft of sunlight in water has no core.
    float u = abs(vUv.x);
    float prof = 1.0 - smoothstep(0.0, 1.0, u);
    prof *= prof;
    // It is already dying as it enters; the water formula finishes it.
    // Faded in at the surface as well as out at the far end: a hard edge
    // where the quad begins reads as the edge of a sheet, not as light.
    float along_ = smoothstep(0.0, 0.16, vUv.y) * (1.0 - smoothstep(0.35, 1.0, vUv.y));
    // And it has to be GONE before it reaches the sea floor, or the floor's
    // depth buffer cuts the quad off square and the shaft ends in a straight
    // horizontal edge - which is exactly what it did.
    along_ *= smoothstep(${FLOOR_Y.toFixed(2)}, ${(FLOOR_Y + 4.5).toFixed(2)}, vWorld.y);

    vec3 col = vec3(1.0, 0.98, 0.88) * prof * along_ * vGain
             * ${RAY_GAIN.toFixed(3)} * water * vSafe;
    float mx = max(col.r, max(col.g, col.b));
    if (mx > ${RAY_PEAK}) col *= ${RAY_PEAK} / mx;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// The suspended matter
// ---------------------------------------------------------------------------

const particleVertexShader = `
  precision mediump float;

  attribute float aSeed;
  attribute float aBubble;

  uniform float uPixelScaleP;
  uniform float uPointSizeMax;
  uniform float uSparkle;        // bands.high
  uniform float uRayCount;
  uniform float uSunTilt;
  uniform float uSunAz;
  uniform float uAgitation;

  varying float vReveal;
  varying vec3 vColor;

  ${WATER_CHUNK}
  ${SWELL_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  void main() {
    vec3 pos = position;

    // Everything in the water drifts on the same swell the blades ride, just
    // far more slowly - it is the same current.
    vec2 drift = swellAt(0.55, aSeed * 6.2831) * 0.55;
    pos.xz += drift;

    if (aBubble > 0.5) {
      // A bubble climbs and wobbles, and starts again at the floor.
      float climb = mod(uTime * (0.55 + aSeed * 0.5) + aSeed * ${WATER_DEPTH.toFixed(1)}, ${WATER_DEPTH.toFixed(1)});
      pos.y = ${FLOOR_Y.toFixed(2)} + climb;
      pos.x += sin(uTime * 2.1 + aSeed * 19.0) * 0.30;
      pos.z += cos(uTime * 1.7 + aSeed * 11.0) * 0.30;
    } else {
      pos.y += sin(uTime * 0.21 + aSeed * 12.0) * 0.55;
    }

    // How close this mote is to a shaft. The shafts are cheap to evaluate
    // (one direction each) and this is what lights the water.
    float best = 0.0;
    for (int i = 0; i < 12; i++) {
      if (float(i) > uRayCount - 0.5) break;
      float fi = float(i);
      float az = uSunAz + fi * 2.39996;
      float wob = ${RAY_REFRACT_AMP.toFixed(5)} * (1.0 + uAgitation * 0.8)
                * sin(${(2 * Math.PI / RAY_REFRACT_PERIOD).toFixed(6)} * uTime + fi * 1.7);
      float tilt = uSunTilt + wob;
      vec3 dir = normalize(vec3(sin(tilt) * sin(az), -cos(tilt), sin(tilt) * cos(az)));
      float r = 4.0 + mod(fi * 6.3, 17.0);
      vec3 origin = vec3(sin(az * 1.7) * r, 0.0, cos(az * 1.7) * r);
      vec3 rel = pos - origin;
      float t = dot(rel, dir);
      float dist = length(rel - dir * max(t, 0.0));
      best = max(best, 1.0 - smoothstep(0.0, ${PARTICLE_REVEAL_R.toFixed(2)}, dist));
    }

    float twinkle = 0.75 + 0.25 * sin(uTime * 3.1 + aSeed * 40.0) * uSparkle;
    vReveal = (0.10 + 0.90 * best) * twinkle;
    vColor = waterAtten(pos) * (aBubble > 0.5 ? vec3(1.0, 1.0, 0.95) : vec3(0.85, 0.95, 0.90));

    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    float world = aBubble > 0.5 ? 0.075 : 0.038 + aSeed * 0.030;
    gl_PointSize = clamp(world * uPixelScaleP / max(-mv.z, 0.001), 1.0, uPointSizeMax);
    gl_Position = projectionMatrix * mv;
    vReveal *= kelpSafeAtt(gl_Position);
  }
`;

const particleFragmentShader = laserDustFragmentShader({ brightness: 1.5, peakLuminance: 0.80 });

// ---------------------------------------------------------------------------
// The surface, seen from underneath
// ---------------------------------------------------------------------------

// No ripple.js exists in this codebase (the brief names one), so the height
// field is four incommensurable sines - which is what a low-resolution ripple
// field amounts to anyway, and it only has to survive being seen edge-on at
// the top of the frame.
const surfaceVertexShader = `
  precision mediump float;

  uniform float uTime;
  uniform float uAgitation;

  varying vec3 vWorld;
  varying vec2 vXZ;
  varying float vSafe;

  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}

  float surfH(vec2 p) {
    float a = 1.0 + uAgitation * 0.9;
    return a * (0.10 * sin(p.x * 0.42 + uTime * 0.53)
              + 0.07 * sin(p.y * 0.61 - uTime * 0.37)
              + 0.05 * sin((p.x + p.y) * 0.29 + uTime * 0.71)
              + 0.03 * sin((p.x - p.y) * 0.83 - uTime * 0.91));
  }

  void main() {
    vec3 p = position;
    p.y += surfH(p.xz);
    vWorld = p;
    vXZ = p.xz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
    vSafe = kelpSafeAtt(gl_Position);
  }
`;

const surfaceFragmentShader = `
  precision mediump float;

  uniform float uTime;
  uniform float uAgitation;
  uniform vec2 uSunXZ;
  uniform float uDepthDebug;

  varying vec3 vWorld;
  varying vec2 vXZ;
  varying float vSafe;

  ${WATER_CHUNK}

  void main() {
    if (uDepthDebug > 0.5) { gl_FragColor = vec4(depthDebug(vWorld), 1.0); return; }

    vec3 V = normalize(cameraPosition - vWorld);
    // Seen from underneath, the surface is a mirror past the critical angle
    // (about 48.6 degrees from vertical) and a window inside it. The silver
    // rim of the window is total internal reflection.
    float up = clamp(V.y, 0.0, 1.0);
    float window_ = smoothstep(0.55, 0.80, up);

    // The ripple is FADED OUT WITH DISTANCE, not made finer. At grazing
    // incidence a ripple term aliases against the mesh into long diagonal
    // bands across the whole frame - and raising its frequency made that
    // worse, not better. Far water is smooth silver; only the patch the diver
    // is under is close enough to resolve a ripple at all.
    float rd = length(cameraPosition - vWorld);
    float rfade = 1.0 - smoothstep(16.0, 46.0, rd);
    float ripple = 0.5 + 0.5 * sin(vXZ.x * 1.7 + uTime * 0.9)
                              * sin(vXZ.y * 1.3 - uTime * 0.7);
    ripple = 0.5 + (ripple - 0.5) * 0.55 * rfade;
    // The sun: a very bright, very soft patch, off centre.
    float sd = length(vXZ - uSunXZ);
    float sun = exp(-sd * sd * 0.020);

    vec3 bright = vec3(0.30, 0.38, 0.40) * (0.55 + 0.45 * ripple);
    vec3 silver = vec3(0.20, 0.25, 0.27) * (0.70 + 0.30 * ripple);
    vec3 col = mix(silver, bright, window_);
    col += vec3(1.0, 0.97, 0.86) * sun * 0.85 * (0.8 + 0.2 * ripple);

    // The surface sits at depth 0, so the brief's own formula leaves it
    // untouched wherever it is - it is the VIEW path that darkens the far
    // water and keeps the bright surface where the diver is looking.
    col *= viewAtten(vWorld) * vSafe;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ---------------------------------------------------------------------------
// The sea floor
// ---------------------------------------------------------------------------

function makeFloorFragmentShader(opts) {
  return `
  precision mediump float;
  #ifdef GL_FRAGMENT_PRECISION_HIGH
    #define CDOM highp
  #else
    #define CDOM mediump
  #endif

  uniform float uFieldTime;
  uniform float uCaustGain;
  uniform float uDepthDebug;

  varying vec3 vWorld;
  varying float vSafe;

  ${WATER_CHUNK}
  ${opts.caustics ? HASH_CHUNK : ''}
  ${opts.caustics ? buildFieldChunk('floorField', opts.octaves) : ''}

  void main() {
    vec3 down = lightAtDepth(vWorld.y);
    if (uDepthDebug > 0.5) { gl_FragColor = vec4(depthDebug(vWorld), 1.0); return; }

    // Rock: barely there, and deliberately so. It exists to be a floor, not
    // to be looked at.
    float g = 0.5 + 0.5 * sin(vWorld.x * 0.9) * sin(vWorld.z * 1.1);
    float g2 = 0.5 + 0.5 * sin(vWorld.x * 3.7 - vWorld.z * 2.3);
    vec3 col = vec3(0.026, 0.030, 0.034) * (0.75 + 0.5 * g) + vec3(0.004) * g2;

    ${opts.caustics ? `
    CDOM vec2 cp = vWorld.xz * ${CAUST_SCALE_FLOOR.toFixed(4)};
    vec3 H;
    floorField(cp, H);
    float trH = H.x + H.z;
    float detH = H.x * H.z - H.y * H.y;
    float d = ${(CAUST_CONVERGE * 1.25).toFixed(2)};
    float j = 1.0 + d * (d * detH - trH);
    float caust = clamp(1.0 - abs(j) * ${CAUST_SHARP.toFixed(3)}, 0.0, 1.0);
    caust *= caust;
    col += vec3(0.50, 0.80, 0.70) * caust * uCaustGain * ${CAUST_GAIN.toFixed(2)};
    ` : ''}

    // A floor 18m down keeps almost nothing: this is the darkest thing in the
    // frame and the reason the top of the frame reads as bright.
    col *= down * viewAtten(vWorld) * vSafe;
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

const floorVertexShader = `
  precision mediump float;
  varying vec3 vWorld;
  varying float vSafe;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_CHUNK}
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorld = wp.xyz;
    gl_Position = projectionMatrix * viewMatrix * wp;
    vSafe = kelpSafeAtt(gl_Position);
  }
`;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

// The current's direction. Fixed: it is the same current for every blade, and
// each blade's phase is read off its position ALONG it (see SWELL_SPATIAL_K),
// which is what turns forty independent plants into one moving forest.
const SWELL_DIR = (function () {
  const v = new THREE.Vector2(0.82, 0.57);
  v.normalize();
  return v;
})();

// ONE ribbon, subdivided into BLADE_SEGMENTS rows and STRIPS columns. There
// are no lateral leaves: the lanières at the top are the ribbon's own strips
// coming apart, which is what a laminaria actually does. The previous build
// hung seven little ribbons off a narrow stipe and read as bamboo.
function buildBladeGeometry() {
  const pos = [], aS = [], aU = [], aStrip = [], idx = [];
  for (let st = 0; st < STRIPS; st++) {
    const u0 = -1 + (2 * st) / STRIPS;
    const u1 = -1 + (2 * (st + 1)) / STRIPS;
    const base = pos.length / 3;
    for (let r = 0; r <= BLADE_SEGMENTS; r++) {
      const sv = r / BLADE_SEGMENTS;
      for (let c = 0; c < 2; c++) {
        pos.push(0, 0, 0);            // the vertex shader owns every position
        aS.push(sv);
        aU.push(c === 0 ? u0 : u1);
        aStrip.push(st);
      }
    }
    for (let r = 0; r < BLADE_SEGMENTS; r++) {
      const p0 = base + r * 2, p1 = p0 + 1, p2 = p0 + 2, p3 = p0 + 3;
      idx.push(p0, p1, p3, p0, p3, p2);
    }
  }
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute('aS', new THREE.BufferAttribute(new Float32Array(aS), 1));
  g.setAttribute('aU', new THREE.BufferAttribute(new Float32Array(aU), 1));
  g.setAttribute('aStrip', new THREE.BufferAttribute(new Float32Array(aStrip), 1));
  g.setIndex(idx);
  return g;
}

function buildRayGeometry(count) {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
  g.setAttribute('aSide', new THREE.BufferAttribute(new Float32Array([-1, 1, 1, -1]), 1));
  g.setAttribute('aSpan', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 1]), 1));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  const iRay = new Float32Array(count);
  for (let i = 0; i < count; i++) iRay[i] = i;
  g.setAttribute('iRay', new THREE.InstancedBufferAttribute(iRay, 1));
  g.instanceCount = count;
  return g;
}

// ---------------------------------------------------------------------------
// Materials, compiled once per tier and never released - three.js frees a
// program the moment its last material is disposed, and this board takes
// seconds to compile. Same pattern as nightdrive.js's MATERIALS. The uniform
// OBJECTS are cached with them: three.js resolves a material's uniform list
// once and keeps what it resolved against, so a swapped uniforms object is
// simply not read.
// ---------------------------------------------------------------------------
const MATERIALS = new Map();

function tierMaterials(tier) {
  const cached = MATERIALS.get(tier);
  if (cached) return cached;
  const L = TIER_LAYOUT[tier];

  const uniforms = Object.assign({}, safeZoneUniforms, {
    uTime: { value: 0 },
    uFieldTime: { value: 0 },
    uWaterDepth: { value: WATER_DEPTH },
    uTurbidity: { value: TURBIDITY },
    uSwellAmp: { value: new THREE.Vector2(SWELL_A1, SWELL_A2) },
    uSwellDir: { value: SWELL_DIR.clone() },
    uCamHeading: { value: 0 },
    uBladeColor: { value: setLinearFromHSL(new THREE.Color(), BLADE_BASE_HUE, BLADE_SAT, BLADE_LIGHTNESS) },
    uTransWarm: { value: new THREE.Color().setRGB(
      TRANSMIT_WARM[0], TRANSMIT_WARM[1], TRANSMIT_WARM[2], THREE.LinearSRGBColorSpace) },
    uSunDir: { value: new THREE.Vector3(0, -1, 0) },
    uSunAz: { value: 0.7 },
    uSunTilt: { value: 0.3 },
    uSunXZ: { value: new THREE.Vector2(6, -4) },
    uPixelScale: { value: 1 },
    uPixelScaleP: { value: 1 },
    uPointSizeMax: { value: 64 },
    uRayCount: { value: L.rays },
    uRayFade: { value: 1 },
    uAgitation: { value: 0 },
    uSparkle: { value: 0 },
    uCaustGain: { value: 1 },
    uDepthDebug: { value: 0 }
  });

  const entry = {
    uniforms: uniforms,
    blade: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: makeBladeVertexShader(),
      fragmentShader: makeBladeFragmentShader({ caustics: L.caustFrond, octaves: L.octaves }),
      // OPAQUE, and that is what makes the rays occlude correctly. The
      // translucency is in the shading - transmitted light, not alpha - so
      // the blades can sit in the opaque pass and write depth, which is the
      // only way a frond can cut a shaft cleanly.
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: L.doubleSide ? THREE.DoubleSide : THREE.FrontSide
    }),
    ray: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: rayVertexShader,
      fragmentShader: rayFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      // depthTest ON against the blades' depth, depthWrite OFF so shafts do
      // not occlude each other. This is criterion 4.
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide
    }),
    particle: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: particleVertexShader,
      fragmentShader: particleFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    }),
    floor: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: floorVertexShader,
      fragmentShader: makeFloorFragmentShader({ caustics: L.caustFloor, octaves: L.octaves }),
      transparent: false,
      depthWrite: true,
      depthTest: true
    }),
    surface: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: surfaceVertexShader,
      fragmentShader: surfaceFragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide
    }),
    urchin: new THREE.MeshBasicMaterial({ color: 0x05070a })
  };
  MATERIALS.set(tier, entry);
  return entry;
}

export class KelpVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // ?kelpswell=<f> pins the swell amplitude.
    const sw = parseFloat(urlParam('kelpswell'));
    this._forcedSwell = Number.isFinite(sw) && sw >= 0 ? sw : null;
    // ?kelprays=0 turns the shafts off, to see what they were carrying.
    this._noRays = urlParam('kelprays') === '0';
    // ?kelpdepth=1 renders the water attenuation as grey, so criterion 3 is a
    // measurement rather than an impression.
    this._depthDebug = urlParam('kelpdepth') === '1';
    // ?kelpsingle=1 puts ONE blade in front of the camera. The travelling
    // swell is a claim about a time series along s and cannot be judged from
    // a forest.
    this._single = urlParam('kelpsingle') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.fov = CAMERA_FOV;
    camera.updateProjectionMatrix();

    const L = TIER_LAYOUT[this._tier];
    this._layout = L;
    this._bladeCount = this._single ? 1 : L.blades;

    const mats = tierMaterials(this._tier);
    this._uniforms = mats.uniforms;
    this._mats = mats;
    this._resetUniforms();

    this._buildFloor();
    this._buildBlades();
    this._buildRays();
    this._buildParticles();
    if (L.surface) this._buildSurface();
    if (!this._single) this._buildUrchins();

    this._time = 0;
    this._lastPhrase = null;
    this._rayFadeDir = 1;
    this._rayFade = 1;
    this._agitation = 0;
    this._sparkle = 0;
    this._swellScale = 1;
    this._lastPaletteRef = undefined;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._lookAt = new THREE.Vector3();
    this._synth = { low: 0.10, mid: 0.10, high: 0.10 };
    this._synthPhase = { low: null, mid: null, high: null };
    this._applyPalette(null);
    this._writeCamera(0);
  }

  _resetUniforms() {
    const u = this._uniforms;
    u.uTime.value = 0;
    u.uFieldTime.value = 0;
    u.uRayCount.value = this._noRays ? 0 : this._layout.rays;
    u.uRayFade.value = 1;
    u.uAgitation.value = 0;
    u.uSparkle.value = 0;
    u.uCaustGain.value = 1;
    u.uSwellAmp.value.set(SWELL_A1, SWELL_A2);
    u.uDepthDebug.value = this._depthDebug ? 1 : 0;
    u.uPointSizeMax.value = 64;
  }

  // --- geometry --------------------------------------------------------------

  _buildBlades() {
    const g = buildBladeGeometry();
    const n = this._bladeCount;
    const iPos = new Float32Array(n * 2);
    const iHeight = new Float32Array(n);
    const iHalf = new Float32Array(n);
    const iPhase = new Float32Array(n);
    const iHue = new Float32Array(n);
    const iNear = new Float32Array(n);

    for (let i = 0; i < n; i++) {
      let r, a;
      if (this._single) {
        r = 0; a = 0;
      } else {
        // Spread over the annulus by area, so the forest does not bunch up
        // near the diver: uniform in r would put half the blades in the
        // nearest quarter of the ground it covers.
        const t = (i + 0.5) / n;
        r = Math.sqrt(BLADE_MIN_R * BLADE_MIN_R
          + t * (BLADE_MAX_R * BLADE_MAX_R - BLADE_MIN_R * BLADE_MIN_R));
        a = (i * 2.39996) + Math.random() * 0.35;
      }
      const x = Math.sin(a) * r, z = Math.cos(a) * r;
      iPos[i * 2] = x;
      iPos[i * 2 + 1] = z;
      // Near blades are big, far ones small - and the water does the rest.
      const near01 = 1 - clamp01((r - BLADE_MIN_R) / (BLADE_MAX_R - BLADE_MIN_R));
      iHeight[i] = this._single
        ? 8.0
        : BLADE_MIN_H + (BLADE_MAX_H - BLADE_MIN_H) * (0.25 + 0.75 * near01) * (0.75 + Math.random() * 0.5);
      iHalf[i] = BLADE_HALF_MAX * (0.45 + 0.55 * near01) * (0.8 + Math.random() * 0.35);
      // THE PHASE IS READ OFF THE POSITION ALONG THE CURRENT. Same swell for
      // every blade; only where it is in that swell differs.
      iPhase[i] = (x * SWELL_DIR.x + z * SWELL_DIR.y) * SWELL_SPATIAL_K
                + (this._single ? 0 : Math.random() * 0.25);
      iHue[i] = Math.random();
      iNear[i] = (!this._single && r < 12.0) ? 1 : 0;
    }

    g.setAttribute('iPos', new THREE.InstancedBufferAttribute(iPos, 2));
    g.setAttribute('iHeight', new THREE.InstancedBufferAttribute(iHeight, 1));
    g.setAttribute('iHalf', new THREE.InstancedBufferAttribute(iHalf, 1));
    g.setAttribute('iPhase', new THREE.InstancedBufferAttribute(iPhase, 1));
    g.setAttribute('iHue', new THREE.InstancedBufferAttribute(iHue, 1));
    g.setAttribute('iNear', new THREE.InstancedBufferAttribute(iNear, 1));
    g.instanceCount = n;

    this._bladeGeometry = g;
    this._blades = new THREE.Mesh(g, this._mats.blade);
    this._blades.frustumCulled = false;
    this._scene.add(this._blades);
    this._bladeData = { iPos, iHeight, iPhase };
  }

  _buildRays() {
    const count = this._noRays ? 0 : this._layout.rays;
    this._rayGeometry = buildRayGeometry(Math.max(1, count));
    this._rays = new THREE.Mesh(this._rayGeometry, this._mats.ray);
    this._rays.frustumCulled = false;
    this._rays.renderOrder = 3;
    this._rays.visible = count > 0;
    this._scene.add(this._rays);
  }

  _buildParticles() {
    const n = this._layout.particles;
    const pos = new Float32Array(n * 3);
    const seed = new Float32Array(n);
    const bubble = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const r = Math.sqrt(Math.random()) * 26;
      const a = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.sin(a) * r;
      pos[i * 3 + 1] = FLOOR_Y + Math.random() * WATER_DEPTH;
      pos[i * 3 + 2] = Math.cos(a) * r;
      seed[i] = Math.random();
      bubble[i] = Math.random() < BUBBLE_FRACTION ? 1 : 0;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    g.setAttribute('aBubble', new THREE.BufferAttribute(bubble, 1));
    this._particleGeometry = g;
    this._particles = new THREE.Points(g, this._mats.particle);
    this._particles.frustumCulled = false;
    this._particles.renderOrder = 4;
    this._scene.add(this._particles);
  }

  _buildFloor() {
    this._floorGeometry = new THREE.PlaneGeometry(140, 140, 1, 1);
    this._floor = new THREE.Mesh(this._floorGeometry, this._mats.floor);
    this._floor.rotation.x = -Math.PI / 2;
    this._floor.position.y = FLOOR_Y;
    this._floor.frustumCulled = false;
    this._scene.add(this._floor);
  }

  _buildSurface() {
    // Coarse: it is seen almost edge-on at the top of the frame, and the
    // height field only has to break its silhouette.
    const seg = this._layout.surfSegs;
    this._surfaceGeometry = new THREE.PlaneGeometry(120, 120, seg, seg);
    this._surface = new THREE.Mesh(this._surfaceGeometry, this._mats.surface);
    this._surface.rotation.x = -Math.PI / 2;
    this._surface.position.y = 0;
    this._surface.frustumCulled = false;
    this._scene.add(this._surface);
  }

  _buildUrchins() {
    const g = new THREE.IcosahedronGeometry(0.26, 0);
    const n = 24;
    this._urchins = new THREE.InstancedMesh(g, this._mats.urchin, n);
    const m = new THREE.Matrix4();
    for (let i = 0; i < n; i++) {
      const r = BLADE_MIN_R + Math.random() * (BLADE_MAX_R - BLADE_MIN_R) * 0.6;
      const a = Math.random() * Math.PI * 2;
      m.makeTranslation(Math.sin(a) * r, FLOOR_Y + 0.22, Math.cos(a) * r);
      this._urchins.setMatrixAt(i, m);
    }
    this._urchins.instanceMatrix.needsUpdate = true;
    this._urchins.frustumCulled = false;
    this._scene.add(this._urchins);
  }

  // --- palette ---------------------------------------------------------------

  // The palette may TINT the kelp; it may not colour it. Hue is pulled 70% of
  // the way back to brown-green and the saturation is capped, so a red cover
  // gives rust-brown blades and never red ones (criterion 7).
  _applyPalette(palette) {
    const list = (palette && palette.dominantColors && palette.dominantColors.length)
      ? palette.dominantColors
      : (palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : null);
    let h = BLADE_BASE_HUE;
    if (list) {
      this._scratch.set(list[0]).getHSL(this._hsl);
      // Circular, so a hue just below 1.0 moves forward through 0 rather than
      // taking the long way round the wheel.
      let d = this._hsl.h - BLADE_BASE_HUE;
      if (d > 0.5) d -= 1; else if (d < -0.5) d += 1;
      // A cover sitting nearly OPPOSITE the kelp's own hue has no meaningful
      // direction to be pulled in, and the shortest path then decides it on a
      // rounding error. Blue (hue 0.62) is 0.51 away from brown-olive, so it
      // went backwards through red and gave maroon kelp - measured. Anything
      // that far away is treated as cool and pulled towards green instead.
      if (Math.abs(d) > PALETTE_OPPOSITE) d = PALETTE_OPPOSITE;
      h = (BLADE_BASE_HUE + d * PALETTE_TINT + 1) % 1;
    }
    // Saturation and lightness are FIXED, not blended. They are what make it
    // a laminaria rather than a lawn, and a vivid cover must not be able to
    // move them.
    setLinearFromHSL(this._uniforms.uBladeColor.value, h, BLADE_SAT, BLADE_LIGHTNESS);
  }

  // --- the dive --------------------------------------------------------------

  _writeCamera(t) {
    const cam = this._camera;
    if (this._single) {
      // One blade, whole, seen from PERPENDICULAR TO THE CURRENT - which is
      // the only direction from which the swell's displacement is lateral
      // rather than towards the lens, and therefore the only direction from
      // which the travelling wave can be seen (or measured) at all.
      const d = 11.0;
      cam.position.set(-SWELL_DIR.y * d, FLOOR_Y + 5.5, SWELL_DIR.x * d);
      this._lookAt.set(0, FLOOR_Y + 5.5, 0);
      cam.lookAt(this._lookAt);
      this._uniforms.uCamHeading.value = 0;
      return;
    }
    const pan = (t / PAN_PERIOD) * Math.PI * 2;
    const pitch = CAM_PITCH + Math.sin((t / BOB_PERIOD) * Math.PI * 2) * BOB_AMOUNT;
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    // Position AND orientation are written every frame. scene-manager's drift
    // re-bases both together whenever either moves, so a visualizer that
    // writes only one of them makes the drift compound.
    cam.position.set(0, CAM_Y, 0);
    this._lookAt.set(cp * Math.sin(pan) * 10, CAM_Y + sp * 10, cp * Math.cos(pan) * 10);
    cam.lookAt(this._lookAt);
    this._uniforms.uCamHeading.value = pan;
  }

  _stepSynthBands(dt, profile) {
    const k = Math.exp(-1.9 * dt);
    const sy = this._synth;
    sy.low = 0.10 + (sy.low - 0.10) * k;
    sy.mid = 0.10 + (sy.mid - 0.10) * k;
    sy.high = 0.10 + (sy.high - 0.10) * k;
    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };
    const bar = profile.barPhase, beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / 4.7) % 1)) sy.low = 1.0;
    if (fired('mid', (t / 3.1) % 1)) sy.mid = 0.85;
    if (fired('high', (t / 6.3) % 1)) sy.high = 0.75;
  }

  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return; // gel complet

    this._time += deltaTime;
    const t = this._time;
    const u = this._uniforms;
    const director = profile.director;

    this._stepSynthBands(deltaTime, profile);
    const bands = this._effectiveBands(director);
    const low = bands && bands.low != null ? bands.low : 0.5;
    const mid = bands && bands.mid != null ? bands.mid : 0.5;
    const high = bands && bands.high != null ? bands.high : 0.5;

    // EVERY band reaction is smoothed over seconds, and none of them is a
    // pulse. The swell does not know the tempo, and a contemplative piece is
    // exactly where a beat-locked twitch would be most obvious.
    const tau = (x, target, s) => x + (target - x) * Math.min(1, deltaTime / s);
    this._swellScale = tau(this._swellScale, 1 + (low - 0.5) * 0.4, 2.2);
    this._agitation = tau(this._agitation, mid, 1.8);
    this._sparkle = tau(this._sparkle, high, 1.2);

    const swell = this._forcedSwell != null ? this._forcedSwell : this._swellScale;
    u.uSwellAmp.value.set(SWELL_A1 * swell, SWELL_A2 * swell);
    u.uAgitation.value = this._agitation;
    u.uSparkle.value = this._sparkle;

    // progress -> the sun's height, so the shafts are oblique at the start,
    // vertical in the middle and oblique the other way at the end.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    // Never less than 7 degrees off vertical: at exactly vertical the
    // caustics lose the obliquity that puts them on the standing fronds.
    const raw = Math.cos(progress * Math.PI) * 28 * DEG;
    const tilt = raw >= 0 ? Math.max(raw, 7 * DEG) : Math.min(raw, -7 * DEG);
    u.uSunTilt.value = tilt;
    u.uSunAz.value = 0.7 + t * 0.004;
    u.uSunDir.value.set(Math.sin(tilt) * Math.sin(u.uSunAz.value), -Math.cos(tilt),
      Math.sin(tilt) * Math.cos(u.uSunAz.value));
    u.uSunXZ.value.set(Math.sin(tilt) * 26 + 6, Math.cos(u.uSunAz.value) * 8 - 4);

    // phrase -> one shaft more or less, eased over four seconds.
    const phrase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrase != null) {
      if (this._lastPhrase != null && phrase < this._lastPhrase) this._rayFadeDir *= -1;
      this._lastPhrase = phrase;
    }
    this._rayFade = clamp01(this._rayFade + (this._rayFadeDir > 0 ? 1 : -1) * deltaTime / 4.0);
    u.uRayFade.value = this._rayFade;

    u.uTime.value = t;
    u.uFieldTime.value = t * (2 * Math.PI / CAUST_PERIOD) * 0.16;
    u.uCaustGain.value = 1;

    const tanHalf = Math.tan((this._camera.fov * DEG) / 2);
    const bufferHeight = getRenderBufferHeight() || 1080;
    const pixelScale = bufferHeight / (2 * tanHalf);
    u.uPixelScale.value = pixelScale;
    u.uPixelScaleP.value = pixelScale;

    this._writeCamera(t);

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
  }

  dispose() {
    this._scene.remove(this._blades);
    this._bladeGeometry.dispose();       // the MATERIALS are cached, not disposed
    this._scene.remove(this._rays);
    this._rayGeometry.dispose();
    this._scene.remove(this._particles);
    this._particleGeometry.dispose();
    this._scene.remove(this._floor);
    this._floorGeometry.dispose();
    if (this._surface) {
      this._scene.remove(this._surface);
      this._surfaceGeometry.dispose();
      this._surface = null;
    }
    if (this._urchins) {
      this._scene.remove(this._urchins);
      this._urchins.geometry.dispose();
      this._urchins.dispose();
      this._urchins = null;
    }
  }
}
